import React, { useState, useEffect } from 'react';
import ahoy from 'ahoy.js';
import {
  Banknote,
  ArrowRightLeft,
  TrendingUp,
  Layers,
  Info,
  Lock,
} from 'lucide-react';

const PROVINCES = [
  { code: 'AB', label: 'Alberta' },
  { code: 'BC', label: 'British Columbia' },
  { code: 'MB', label: 'Manitoba' },
  { code: 'NB', label: 'New Brunswick' },
  { code: 'NL', label: 'Newfoundland and Labrador' },
  { code: 'NS', label: 'Nova Scotia' },
  { code: 'NT', label: 'Northwest Territories' },
  { code: 'NU', label: 'Nunavut' },
  { code: 'ON', label: 'Ontario' },
  { code: 'PE', label: 'Prince Edward Island' },
  { code: 'QC', label: 'Quebec' },
  { code: 'SK', label: 'Saskatchewan' },
  { code: 'YT', label: 'Yukon' },
];

const STRUCTURE_OPTIONS = [
  {
    id: 'cash',
    icon: Banknote,
    title: 'Full Cash at Closing',
    description: 'Buyer pays 100% on closing day',
  },
  {
    id: 'seller_financing',
    icon: ArrowRightLeft,
    title: 'Seller Financing',
    description: 'You carry a portion of the purchase price',
  },
  {
    id: 'earnout',
    icon: TrendingUp,
    title: 'Earnout',
    description: 'Part of the price is tied to future performance',
  },
  {
    id: 'blended',
    icon: Layers,
    title: 'Blended',
    description: 'Combine cash, seller note, and earnout',
  },
];

const EXPECTED_CLOSE_OPTIONS = [
  { value: 3, label: '3 months' },
  { value: 6, label: '6 months' },
  { value: 9, label: '9 months' },
  { value: 12, label: '12 months' },
];

// Province name to code mapping for valuation snapshots (which store full names)
const PROVINCE_NAME_TO_CODE = {
  'Alberta': 'AB',
  'British Columbia': 'BC',
  'Manitoba': 'MB',
  'New Brunswick': 'NB',
  'Newfoundland and Labrador': 'NL',
  'Nova Scotia': 'NS',
  'Northwest Territories': 'NT',
  'Nunavut': 'NU',
  'Ontario': 'ON',
  'Prince Edward Island': 'PE',
  'Quebec': 'QC',
  'Saskatchewan': 'SK',
  'Yukon': 'YT',
};

function mapProvinceToCode(province) {
  if (!province) return 'ON';
  // Already a code (2 chars)
  if (province.length <= 3) return province;
  return PROVINCE_NAME_TO_CODE[province] || 'ON';
}

function formatSnapshotCurrency(value) {
  const num = parseFloat(value);
  if (isNaN(num)) return '$0';
  if (num >= 1000000) return `$${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `$${(num / 1000).toFixed(0)}K`;
  return `$${num.toFixed(0)}`;
}

const DealStructureWizard = ({
  onCalculate,
  loading = false,
  defaultProvince = 'ON',
  initialValues = null,
  defaultAskingPrice = null,
  valuationSnapshots = [],
  dealStructureSnapshots = [],
  onLoadDealStructureSnapshot,
}) => {
  const hasValuationSnapshots = valuationSnapshots.length > 0;
  const hasDealStructureSnapshots = dealStructureSnapshots.length > 0;
  const hasAnySnapshots = hasValuationSnapshots || hasDealStructureSnapshots;
  const [step, setStep] = useState(hasAnySnapshots ? 0 : 1);
  const [selectedValuationSnapshot, setSelectedValuationSnapshot] = useState(null);
  const [askingPrice, setAskingPrice] = useState(
    initialValues?.askingPrice ||
      (defaultAskingPrice ? String(defaultAskingPrice) : '750000')
  );
  const [expectedCloseMonths, setExpectedCloseMonths] = useState(
    initialValues?.expectedCloseMonths || 6
  );
  const [structure, setStructure] = useState(initialValues?.structure || null);
  const [province, setProvince] = useState(
    initialValues?.province || defaultProvince || 'ON'
  );

  // Step 2 fields
  const [downPayment, setDownPayment] = useState(
    initialValues?.downPayment || ''
  );
  const [repaymentTermYears, setRepaymentTermYears] = useState(
    initialValues?.repaymentTermYears || 5
  );
  const [interestRate, setInterestRate] = useState(
    initialValues?.interestRate || '5.0'
  );
  const [earnoutAmount, setEarnoutAmount] = useState(
    initialValues?.earnoutAmount || ''
  );
  const [earnoutDurationYears, setEarnoutDurationYears] = useState(
    initialValues?.earnoutDurationYears || 2
  );

  useEffect(() => {
    ahoy.track('succeedo_deal_structure_started');
  }, []);

  const askingPriceNum = parseFloat(askingPrice) || 0;
  const downPaymentNum = parseFloat(downPayment) || 0;
  const earnoutAmountNum = parseFloat(earnoutAmount) || 0;

  // Auto-compute seller note = asking_price - down_payment - earnout
  const computedSellerNote = Math.max(
    0,
    askingPriceNum - downPaymentNum - (structure === 'blended' ? earnoutAmountNum : 0)
  );

  // Auto-set default down payment when entering step 2
  useEffect(() => {
    if (!downPayment && askingPriceNum > 0) {
      setDownPayment(String(Math.round(askingPriceNum * 0.75)));
    }
  }, [step]);

  const totalSteps = structure === 'cash' ? 1 : 2;
  const progress = step > 0 ? (step / totalSteps) * 100 : 0;

  const showSellerNote =
    structure === 'seller_financing' || structure === 'blended';
  const showEarnout = structure === 'earnout' || structure === 'blended';

  const handleContinue = () => {
    if (step === 1) {
      if (structure === 'cash') {
        // Skip step 2, go directly to results
        handleSubmit();
      } else {
        setStep(2);
      }
    }
  };

  const handleBack = () => {
    if (step === 1 && hasAnySnapshots) {
      setStep(0);
    } else if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleSubmit = () => {
    const params = {
      askingPrice: askingPriceNum,
      structure,
      province,
      expectedCloseMonths,
    };

    if (showSellerNote) {
      params.downPayment = downPaymentNum;
      params.sellerNoteAmount = computedSellerNote;
      params.repaymentTermYears = repaymentTermYears;
      params.interestRate = parseFloat(interestRate) || 5.0;
    }

    if (showEarnout) {
      params.earnoutAmount = earnoutAmountNum;
      params.earnoutDurationYears = earnoutDurationYears;
    }

    if (structure === 'cash') {
      params.downPayment = askingPriceNum;
    }

    if (onCalculate) {
      onCalculate(params, selectedValuationSnapshot);
    }
  };

  const formatNumber = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return '';
    return num.toLocaleString();
  };

  // Handler for selecting a valuation snapshot at Step 0
  const handleSelectSnapshot = (snap) => {
    setSelectedValuationSnapshot(snap);
    const range = snap.value_range || {};
    const midpoint = Math.round(
      ((parseFloat(range.low) || 0) + (parseFloat(range.high) || 0)) / 2
    );
    if (midpoint > 0) setAskingPrice(String(midpoint));

    const snapInputs = snap.inputs || {};
    const snapProvince = mapProvinceToCode(snapInputs.province);
    if (snapProvince) setProvince(snapProvince);

    setStep(1);
  };

  return (
    <div className="flex items-center justify-center p-6">
      <div className="bg-card border border-border rounded-[16px] p-8 max-w-[700px] w-full">
        {/* Step Indicator — hidden on Step 0 */}
        {step > 0 && (
          <div className="mb-8">
            <div className="font-mono text-[11px] uppercase text-muted-foreground mb-3 tracking-wide">
              STEP {step} OF {totalSteps} &mdash;{' '}
              {step === 1 ? 'DEAL TERMS' : 'PAYMENT DETAILS'}
            </div>
            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{
                  width: `${progress}%`,
                  background:
                    'linear-gradient(to right, hsl(var(--succeedo-orange)), hsl(var(--succeedo-orange) / 0.7))',
                }}
              />
            </div>
          </div>
        )}

        {/* Step 0: Snapshot Selection */}
        {step === 0 && (
          <div>
            <h2 className="font-serif text-[32px] font-light text-foreground tracking-[-0.02em] mb-2">
              Deal Structure
            </h2>
            <p className="text-[16px] text-muted-foreground mb-8">
              Pick up where you left off, start from a valuation, or build from scratch.
            </p>

            {/* Saved Deal Structure Snapshots */}
            {hasDealStructureSnapshots && (
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <div
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ backgroundColor: 'hsl(var(--succeedo-orange))' }}
                  />
                  <span className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide">
                    YOUR SAVED SCENARIOS
                  </span>
                </div>

                <div className="space-y-3">
                  {dealStructureSnapshots.slice(0, 3).map((snap) => {
                    const date = snap.created_at
                      ? new Date(snap.created_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })
                      : '';
                    const structureLabel = (snap.structure_type || 'cash')
                      .replace(/_/g, ' ')
                      .replace(/\b\w/g, (c) => c.toUpperCase());
                    const afterTax = snap.after_tax_proceeds || snap.results?.after_tax_proceeds;

                    return (
                      <button
                        key={snap.id}
                        type="button"
                        onClick={() => onLoadDealStructureSnapshot && onLoadDealStructureSnapshot(snap)}
                        className="w-full text-left bg-background border border-input rounded-[12px] p-4 hover:border-[hsl(var(--succeedo-orange))] cursor-pointer transition-all"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="text-[13px] text-muted-foreground">{date}</span>
                            <span className="font-mono text-[10px] uppercase border border-border rounded-full px-2 py-0.5 text-muted-foreground">
                              {structureLabel}
                            </span>
                            {snap.is_primary && (
                              <span
                                className="font-mono text-[10px] uppercase rounded-full px-2 py-0.5"
                                style={{
                                  backgroundColor: 'hsl(var(--succeedo-orange) / 0.1)',
                                  color: 'hsl(var(--succeedo-orange))',
                                  border: '1px solid hsl(var(--succeedo-orange) / 0.2)',
                                }}
                              >
                                &#9733; Active
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="text-[15px] font-semibold text-foreground">
                              {afterTax ? formatSnapshotCurrency(afterTax) : '--'} after tax
                            </span>
                            <span
                              className="text-[13px] font-medium"
                              style={{ color: 'hsl(var(--succeedo-orange))' }}
                            >
                              Open &rarr;
                            </span>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Valuation Snapshots — start new deal from valuation */}
            {hasValuationSnapshots && (
              <div className="mb-8">
                {hasDealStructureSnapshots && (
                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-border" />
                    </div>
                    <div className="relative flex justify-center">
                      <span className="bg-card px-3 font-mono text-[10px] uppercase text-muted-foreground/40">
                        or start new
                      </span>
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-2 mb-4">
                  <div
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ backgroundColor: 'hsl(var(--succeedo-orange))' }}
                  />
                  <span className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide">
                    START FROM A VALUATION
                  </span>
                </div>

                <div className="space-y-3">
                  {valuationSnapshots.slice(0, 3).map((snap) => {
                    const range = snap.value_range || {};
                    const date = snap.created_at
                      ? new Date(snap.created_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })
                      : '';
                    const sector = (snap.sector_cluster || '')
                      .replace(/_/g, ' ')
                      .replace(/\b\w/g, (c) => c.toUpperCase());
                    const displayValue =
                      snap.ebitda_value != null
                        ? formatSnapshotCurrency(snap.ebitda_value)
                        : `${formatSnapshotCurrency(range.low || 0)} \u2014 ${formatSnapshotCurrency(range.high || 0)}`;

                    return (
                      <button
                        key={snap.id}
                        type="button"
                        onClick={() => handleSelectSnapshot(snap)}
                        className="w-full text-left bg-background border border-input rounded-[12px] p-4 hover:border-[hsl(var(--succeedo-orange))] cursor-pointer transition-all"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="text-[13px] text-muted-foreground">{date}</span>
                            {sector && (
                              <span className="font-mono text-[10px] uppercase border border-border rounded-full px-2 py-0.5 text-muted-foreground">
                                {sector}
                              </span>
                            )}
                            {snap.is_primary && (
                              <span
                                className="font-mono text-[10px] uppercase rounded-full px-2 py-0.5"
                                style={{
                                  backgroundColor: 'hsl(var(--succeedo-orange) / 0.1)',
                                  color: 'hsl(var(--succeedo-orange))',
                                  border: '1px solid hsl(var(--succeedo-orange) / 0.2)',
                                }}
                              >
                                &#9733; Active
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="text-[15px] font-semibold text-foreground">
                              {displayValue}
                            </span>
                            <span
                              className="text-[13px] font-medium"
                              style={{ color: 'hsl(var(--succeedo-orange))' }}
                            >
                              Select &rarr;
                            </span>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Divider + Start from Scratch */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-card px-3 font-mono text-[10px] uppercase text-muted-foreground/40">
                  or
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setStep(1)}
              className="w-full border border-border text-foreground rounded-full px-6 py-3 font-medium hover:bg-accent transition-colors"
            >
              Start from Scratch &rarr;
            </button>
          </div>
        )}

        {/* Step 1: Deal Terms */}
        {step === 1 && (
          <div>
            <h2 className="font-serif text-[32px] font-light text-foreground tracking-[-0.02em] mb-6">
              How much is the deal worth?
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              {/* Asking Price */}
              <div>
                <label className="block text-[13px] text-muted-foreground mb-2">
                  Asking Price
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">
                    $
                  </span>
                  <input
                    type="text"
                    value={askingPrice}
                    onChange={(e) =>
                      setAskingPrice(e.target.value.replace(/[^0-9]/g, ''))
                    }
                    placeholder="750,000"
                    className="w-full bg-muted border border-input rounded-[8px] pl-8 pr-4 py-3 text-foreground focus:outline-none focus:border-[hsl(var(--succeedo-orange))] transition-colors"
                  />
                </div>
                {selectedValuationSnapshot ? (
                  <p className="text-[12px] text-muted-foreground mt-1">
                    From your{' '}
                    {(selectedValuationSnapshot.sector_cluster || '')
                      .replace(/_/g, ' ')
                      .replace(/\b\w/g, (c) => c.toUpperCase())}{' '}
                    valuation
                  </p>
                ) : defaultAskingPrice ? (
                  <p className="text-[12px] text-muted-foreground mt-1">
                    Based on your most recent valuation estimate
                  </p>
                ) : (
                  <a
                    href="/succeedo/valuation"
                    className="text-[12px] text-[hsl(var(--succeedo-orange))] hover:underline mt-1 inline-block"
                  >
                    Get a valuation first &rarr;
                  </a>
                )}
              </div>

              {/* Expected Close */}
              <div>
                <label className="block text-[13px] text-muted-foreground mb-2">
                  Expected Close
                </label>
                <select
                  value={expectedCloseMonths}
                  onChange={(e) =>
                    setExpectedCloseMonths(parseInt(e.target.value, 10))
                  }
                  className="w-full bg-muted border border-input rounded-[8px] px-4 py-3 text-foreground focus:outline-none focus:border-[hsl(var(--succeedo-orange))] transition-colors appearance-none"
                >
                  {EXPECTED_CLOSE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Structure Selection */}
            <div className="mb-8">
              <div className="font-mono text-[11px] uppercase text-muted-foreground mb-4 tracking-wide">
                PAYMENT STRUCTURE
              </div>
              <div className="grid grid-cols-2 gap-4">
                {STRUCTURE_OPTIONS.map((option) => {
                  const Icon = option.icon;
                  const isSelected = structure === option.id;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => setStructure(option.id)}
                      className={`rounded-[16px] p-5 text-left transition-all ${
                        isSelected
                          ? 'bg-[hsl(var(--succeedo-orange))]/5 border-2 border-[hsl(var(--succeedo-orange))]'
                          : 'bg-muted border border-border hover:border-foreground/20'
                      }`}
                    >
                      <Icon
                        className={`w-6 h-6 mb-3 ${
                          isSelected
                            ? 'text-[hsl(var(--succeedo-orange))]'
                            : 'text-muted-foreground'
                        }`}
                        strokeWidth={2}
                      />
                      <div className="text-[15px] font-medium text-foreground mb-1">
                        {option.title}
                      </div>
                      <div className="text-[13px] text-muted-foreground">
                        {option.description}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Province Selector */}
            <div className="mb-8">
              <label className="block text-[13px] text-muted-foreground mb-2">
                Province (for tax calculation)
              </label>
              <select
                value={province}
                onChange={(e) => setProvince(e.target.value)}
                className="w-full bg-muted border border-input rounded-[8px] px-4 py-3 text-foreground focus:outline-none focus:border-[hsl(var(--succeedo-orange))] transition-colors appearance-none"
              >
                {PROVINCES.map((prov) => (
                  <option key={prov.code} value={prov.code}>
                    {prov.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Navigation */}
            <div className={`flex items-center ${hasAnySnapshots ? 'justify-between' : 'justify-end'}`}>
              {hasAnySnapshots && (
                <button
                  type="button"
                  onClick={() => setStep(0)}
                  className="text-[14px] text-muted-foreground hover:text-foreground transition-colors"
                >
                  &larr; Back
                </button>
              )}
              <button
                type="button"
                onClick={handleContinue}
                disabled={!structure || loading}
                className={`${hasAnySnapshots ? '' : 'w-full '}bg-foreground text-background rounded-full px-6 py-[10px] text-[14px] font-medium hover:bg-foreground/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {loading
                  ? 'Calculating...'
                  : structure === 'cash'
                  ? 'See My Results \u2192'
                  : 'Continue \u2192'}
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Payment Details */}
        {step === 2 && (
          <div>
            <h2 className="font-serif text-[32px] font-light text-foreground tracking-[-0.02em] mb-6">
              {showSellerNote && showEarnout
                ? 'Structure the deal'
                : showSellerNote
                ? 'Structure the seller note'
                : 'Define the earnout'}
            </h2>

            {/* Info Callout */}
            {showSellerNote && (
              <div className="bg-[hsl(var(--succeedo-orange))]/5 border border-[hsl(var(--succeedo-orange))]/20 rounded-[12px] p-4 mb-6 flex gap-3">
                <Info className="w-4 h-4 text-[hsl(var(--succeedo-orange))] flex-shrink-0 mt-0.5" />
                <p className="text-[13px] text-muted-foreground">
                  Seller financing typically covers 20-40% of the deal. A lawyer
                  can draft the promissory note.
                </p>
              </div>
            )}

            <div className="space-y-6 mb-8">
              {/* Seller Financing Fields */}
              {showSellerNote && (
                <>
                  {/* Down Payment */}
                  <div>
                    <label className="block text-[13px] text-muted-foreground mb-2">
                      Down Payment (Buyer)
                    </label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">
                        $
                      </span>
                      <input
                        type="text"
                        value={downPayment}
                        onChange={(e) =>
                          setDownPayment(
                            e.target.value.replace(/[^0-9]/g, '')
                          )
                        }
                        placeholder="562,500"
                        className="w-full bg-muted border border-input rounded-[8px] pl-8 pr-4 py-3 text-foreground focus:outline-none focus:border-[hsl(var(--succeedo-orange))] transition-colors"
                      />
                    </div>
                  </div>

                  {/* Seller Note Amount (computed, read-only) */}
                  <div>
                    <label className="block text-[13px] text-muted-foreground mb-2">
                      Seller Note Amount
                    </label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">
                        $
                      </span>
                      <input
                        type="text"
                        value={formatNumber(computedSellerNote)}
                        readOnly
                        className="w-full bg-muted/60 border border-input rounded-[8px] pl-8 pr-10 py-3 text-foreground cursor-not-allowed"
                      />
                      <Lock className="absolute right-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground/50" />
                    </div>
                  </div>

                  {/* Repayment Term */}
                  <div>
                    <label className="block text-[13px] text-muted-foreground mb-2">
                      Repayment Term
                    </label>
                    <div className="flex items-center gap-2">
                      {[2, 3, 5].map((term) => (
                        <button
                          key={term}
                          type="button"
                          onClick={() => setRepaymentTermYears(term)}
                          className={`flex-1 font-mono text-[11px] uppercase px-4 py-2 transition-colors ${
                            repaymentTermYears === term
                              ? 'bg-foreground text-background rounded-full'
                              : 'border border-border text-muted-foreground rounded-full hover:border-foreground/30'
                          }`}
                        >
                          {term} YRS
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Interest Rate */}
                  <div>
                    <label className="block text-[13px] text-muted-foreground mb-2">
                      Interest Rate on Note
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={interestRate}
                        onChange={(e) =>
                          setInterestRate(
                            e.target.value.replace(/[^0-9.]/g, '')
                          )
                        }
                        placeholder="5.0"
                        className="w-full bg-muted border border-input rounded-[8px] px-4 pr-8 py-3 text-foreground focus:outline-none focus:border-[hsl(var(--succeedo-orange))] transition-colors"
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground">
                        %
                      </span>
                    </div>
                  </div>
                </>
              )}

              {/* Earnout Fields */}
              {showEarnout && (
                <>
                  {/* Earnout Amount */}
                  <div>
                    <label className="block text-[13px] text-muted-foreground mb-2">
                      Earnout Amount
                    </label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">
                        $
                      </span>
                      <input
                        type="text"
                        value={earnoutAmount}
                        onChange={(e) =>
                          setEarnoutAmount(
                            e.target.value.replace(/[^0-9]/g, '')
                          )
                        }
                        placeholder="100,000"
                        className="w-full bg-muted border border-input rounded-[8px] pl-8 pr-4 py-3 text-foreground focus:outline-none focus:border-[hsl(var(--succeedo-orange))] transition-colors"
                      />
                    </div>
                  </div>

                  {/* Earnout Duration */}
                  <div>
                    <label className="block text-[13px] text-muted-foreground mb-2">
                      Earnout Duration
                    </label>
                    <div className="flex items-center gap-2">
                      {[1, 2, 3].map((duration) => (
                        <button
                          key={duration}
                          type="button"
                          onClick={() => setEarnoutDurationYears(duration)}
                          className={`flex-1 font-mono text-[11px] uppercase px-4 py-2 transition-colors ${
                            earnoutDurationYears === duration
                              ? 'bg-foreground text-background rounded-full'
                              : 'border border-border text-muted-foreground rounded-full hover:border-foreground/30'
                          }`}
                        >
                          {duration} {duration === 1 ? 'YR' : 'YRS'}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Down Payment for earnout-only (no seller note) */}
              {structure === 'earnout' && (
                <div>
                  <label className="block text-[13px] text-muted-foreground mb-2">
                    Cash at Closing
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">
                      $
                    </span>
                    <input
                      type="text"
                      value={downPayment}
                      onChange={(e) =>
                        setDownPayment(
                          e.target.value.replace(/[^0-9]/g, '')
                        )
                      }
                      placeholder={String(
                        Math.round(askingPriceNum - earnoutAmountNum)
                      )}
                      className="w-full bg-muted border border-input rounded-[8px] pl-8 pr-4 py-3 text-foreground focus:outline-none focus:border-[hsl(var(--succeedo-orange))] transition-colors"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={handleBack}
                className="text-[14px] text-muted-foreground hover:text-foreground transition-colors"
              >
                &larr; Back
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading}
                className="bg-foreground text-background rounded-full px-6 py-[10px] text-[14px] font-medium hover:bg-foreground/90 transition-colors disabled:opacity-50"
              >
                {loading ? 'Calculating...' : 'See My Results \u2192'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DealStructureWizard;
